<template>
  <v-container>
    <v-container class="pt-0">
      <v-row>
        <v-col cols="12" md="6" class="pt-0">
          <v-switch
            v-model="rotateOnError"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @change="saveDisabled=false;"
            :label="$t('Rotate On Error')"
            dense
          ></v-switch>
        </v-col>
        <v-col cols="12" md="6" class="text-right hidden-sm-and-down">
          <v-btn
            class="mr-4"
            color="grey darken-1"
            :loading="reloadLoading"
            v-if="savingEnable"
            @click="reload"
            outlined
            tile
          >
            <v-icon left>mdi-autorenew</v-icon>{{ $t("reload") }}
          </v-btn>
          <v-btn
            color="primaryLigth"
            :loading="saveLoading"
            :disabled="saveDisabled"
            v-if="savingEnable"
            @click="save"
            outlined
            tile
          >
            <v-icon left>mdi-content-save-outline</v-icon>{{ $t("save") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-select
            v-model="transcoding"
            :items="transcodingItems"
            :loading="firstLoading"
            :disabled="!savingEnable"
            :label="$t('Ingest Transcoding')"
            @change="saveDisabled=false;"
            dense
          ></v-select>
        </v-col>
      </v-row>

      <v-subheader class="pl-0">{{ $t("Uncompressed Images Cache") }}</v-subheader>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="imagesCachePath"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @input="saveDisabled=false;"
            :label="$t('Path')"
            type="text"
            dense
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="imagesCacheSizeHigh"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @input="saveDisabled=false;"
            :label="$t('High Watermark Size (MB)')"
            type="number"
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="imagesCacheSizeLow"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @input="saveDisabled=false;"
            :label="$t('Low Watermark Size (MB)')"
            type="number"
            dense
          ></v-text-field>
        </v-col>
      </v-row>

      <v-subheader class="pl-0">{{ $t("DICOM Tags Cache") }}</v-subheader>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="tagsCachePath"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @input="saveDisabled=false;"
            :label="$t('Path')"
            type="text"
            dense
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="tagsCacheSizeHigh"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @input="saveDisabled=false;"
            :label="$t('High Watermark Size (MB)')"
            type="number"
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="tagsCacheSizeLow"
            :loading="firstLoading"
            :class="{'disable-events': !savingEnable}"
            @input="saveDisabled=false;"
            :label="$t('Low Watermark Size (MB)')"
            type="number"
            dense
          ></v-text-field>
        </v-col>
      </v-row>

      <v-subheader class="pl-0">{{ $t("Datastores") }}</v-subheader>

      <v-row>
        <v-col cols="12" md="6">
          <v-select
            v-model="selectedDatastore"
            :items="selectedDatastoreItems"
            :loading="firstLoading"
            :disabled="!savingEnable"
            :label="$t('Selected')"
            @change="saveDisabled=false;"
            dense
          ></v-select>
        </v-col>
        <v-col cols="12" md="6" class="text-right">
          <v-btn color="primaryLigth" @click="addDatastoreModal" v-if="savingEnable" outlined tile>
            <v-icon left>mdi-plus</v-icon>{{ $t("add datastore") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-data-table
      :headers="datastoresHeader"
      :items="datastores"
      :items-per-page="15"
      @click:row="updateDatastoreModal"
      class="mb-4"
      :footer-props="{ 
        itemsPerPageText: $t('Datastores per page:'),
        itemsPerPageAllText: $t('All'),
      }"
    >
      <template v-slot:[`item.enabled`]="{ item }">
        <v-checkbox
          v-model="item.enabled"
          :disabled="!savingEnable"
          v-on:click.stop="saveDisabled=false"
          inset
        ></v-checkbox>
      </template>
    </v-data-table>
    <v-container class="text-right hidden-md-and-up pl-6 pr-6 mb-6">
      <v-btn
        class="mr-4"
        color="grey darken-1"
        :loading="reloadLoading"
        @click="reload"
        outlined
        tile
      >
        <v-icon left>mdi-autorenew</v-icon>{{ $t("reload") }}
      </v-btn>
      <v-btn
        color="primaryLigth"
        :loading="saveLoading"
        :disabled="saveDisabled"
        v-if="savingEnable"
        @click="save"
        outlined
        tile
      >
        <v-icon left>mdi-content-save-outline</v-icon>{{ $t("save") }}
      </v-btn>
    </v-container>
    <DatastoreModal
      ref="datastoreModal"
      @new="addDatastore"
      @update="updateDatastore"
      @remove="removeDatastore"
    ></DatastoreModal>
  </v-container>
</template>

<script>
import axios from "axios";
import i18n from "../../i18n";
import DatastoreModal from "./DatastoreModal";
import Transcoding from "../../lists/transcoding";
import EventBus from "../../event-bus";

export default {
  components: {
    DatastoreModal,
  },
  data: () => ({
    firstLoading: true,
    saveLoading: false,
    saveDisabled: true,
    reloadLoading: false,
    rotateOnError: true,
    selectedDatastore: null,
    transcoding: null,
    transcodingItems: Transcoding,
    imagesCachePath: null,
    imagesCacheSizeLow: 0,
    imagesCacheSizeHigh: 0,
    tagsCachePath: null,
    tagsCacheSizeLow: 0,
    tagsCacheSizeHigh: 0,
    datastoresHeader: [
      { text: i18n.t("Order"), value: "order" },
      { text: i18n.t("Label"), value: "label" },
      { text: i18n.t("Type"), value: "dtype" },
      { text: i18n.t("Path / Bucket"), value: "pathOrBucket" },
      { text: i18n.t("Enabled"), value: "enabled" },
    ],
    datastores: [],
    datastoreSearch: null,
    counter: 0,
    savingEnable: false
  }),
  mounted() {
    // Permissions
    let configuration = this.$store.getters.resources.configuration;

    if (configuration.storage.update) {
      this.savingEnable = true;
    }

    this.loadData();
  },
  computed: {
    selectedDatastoreItems: function () {
      var list = new Array();
      this.datastores.forEach(function (datastore) {
        list.push(datastore.label);
      });
      return list;
    },
  },
  methods: {
    sendError(error) {
      if (error.response) {
        if (
          error.response.status == 500 &&
          error.response.data.status == "error"
        ) {
          EventBus.$emit("SHOW_ERROR", error.response.data.error);
        } else {
          EventBus.$emit("SHOW_ERROR", error.response.data);
        }
      } else {
        EventBus.$emit("SHOW_ERROR", error);
      }
    },
    loadData() {
      axios({
        url: "/api/v1/config/storage",
        method: "GET",
      })
        .then((response) => {
          // add id
          var datastores = response.data.storage.datastores;
          var counter = 0;
          datastores.forEach(function (datastore) {
            if (datastore.dtype == "FileSystem") {
              datastore.pathOrBucket = datastore.folderPath;
            } else {
              datastore.pathOrBucket = datastore.s3Bucket;
            }
            datastore.id = counter++;
          });
          this.datastores = datastores;
          this.counter = counter;
          // load storage data
          this.rotateOnError = response.data.storage.rotateOnError;
          this.transcoding = response.data.storage.transcoding;
          this.imagesCachePath = response.data.storage.imagesCachePath;
          this.tagsCachePath = response.data.storage.tagsCachePath;
          this.imagesCacheSizeHigh = Number(response.data.storage.imagesCacheSizeHigh);
          this.imagesCacheSizeLow = Number(response.data.storage.imagesCacheSizeLow);
          this.tagsCacheSizeHigh = Number(response.data.storage.tagsCacheSizeHigh);
          this.tagsCacheSizeLow = Number(response.data.storage.tagsCacheSizeLow);
          this.selectedDatastore = response.data.storage.selected;
          // stop loadings
          this.reloadLoading = false;
          this.firstLoading = false;
          this.saveDisabled = true;
        })
        .catch((error) => {
          this.reloadLoading = false;
          this.firstLoading = false;
          this.sendError(error);
        });
    },
    addDatastoreModal() {
      let maxOrder = 0;
      this.counter = this.counter + 1;
      this.datastores.forEach(function (datastore) {
        if (datastore.order > maxOrder) maxOrder = datastore.order;
      });
      this.$refs.datastoreModal.show(null, this.counter, maxOrder);
    },
    updateDatastoreModal(data) {
      this.$refs.datastoreModal.show(data);
    },
    addDatastore(data) {
      this.saveDisabled = false;
      this.datastores.push(data);
    },
    updateDatastore(data) {
      this.saveDisabled = false;
      this.datastores.forEach(function (datastore) {
        if (datastore.id == data.id) {
          datastore.order = Number(data.order);
          datastore.label = data.label;
          datastore.dtype = data.dtype;
          datastore.folderPath = data.folderPath;
          datastore.folderMaxSize = Number(data.folderMaxSize);
          datastore.s3Endpoint = data.s3Endpoint;
          datastore.s3Region = data.s3Region;
          datastore.s3AccessKey = data.s3AccessKey;
          datastore.s3SecretKey = data.s3SecretKey;
          datastore.s3Bucket = data.s3Bucket;
          datastore.s3PathStyle = data.s3PathStyle;
          datastore.pathOrBucket = data.pathOrBucket;
        }
      });
    },
    removeDatastore(data) {
      this.saveDisabled = false;
      this.datastores = this.datastores.filter(function (el) {
        return el.id != data.id;
      });
    },
    reload() {
      this.reloadLoading = true;
      this.loadData();
    },
    save() {
      this.saveLoading = true;

      // send configuration
      axios({
        url: "/api/v1/config/storage",
        method: "PUT",
        data: {
          rotateOnError: this.rotateOnError,
          transcoding: this.transcoding,
          imagesCachePath: this.imagesCachePath,
          tagsCachePath: this.tagsCachePath,
          imagesCacheSizeHigh: Number(this.imagesCacheSizeHigh),
          imagesCacheSizeLow: Number(this.imagesCacheSizeLow),
          tagsCacheSizeHigh: Number(this.tagsCacheSizeHigh),
          tagsCacheSizeLow: Number(this.tagsCacheSizeLow),
          selected: this.selectedDatastore,
          datastores: this.datastores,
        },
      })
        .then((response) => {
          this.saveLoading = false;
          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            this.saveDisabled = true;
          }
        })
        .catch((error) => {
          this.saveLoading = false;
          this.sendError(error);
        });
    },
    checkNested(obj, level, ...rest) {
      if (obj === undefined) return false;
      if (rest.length == 0 && {}.hasOwnProperty.call(obj, level)) return true;
      return this.checkNested(obj[level], ...rest);
    },
  },
};
</script>

<style scoped>
.disable-events {
  pointer-events: none;
}
</style>